import { updatePreset } from "@primeuix/themes";
import type { UserThemeColors } from "~/types/User";
import {
  buildPrimaryScale,
  buildSecondaryScale,
  DEFAULT_MENU_HOVER,
  DEFAULT_THEME_COLORS,
  getContrastColor,
  normalizeHexColor,
  type ResolvedThemeColors,
} from "~/utils/themeColorScale";

let isThemeWatcherRegistered = false;
// The preset already holds the default colors. The store subscription also fires on
// table preference changes, and each updatePreset re-injects every PrimeVue style.
const colorsKey = (colors: ResolvedThemeColors) => `${colors.primary}|${colors.secondary}|${colors.menuHover ?? ""}`;
let appliedKey = colorsKey(DEFAULT_THEME_COLORS);

function resolveThemeColors(colors?: UserThemeColors | null): ResolvedThemeColors {
  const menuHover = normalizeHexColor(colors?.menuHover);

  return {
    primary: normalizeHexColor(colors?.primary) ?? DEFAULT_THEME_COLORS.primary,
    secondary: normalizeHexColor(colors?.secondary) ?? DEFAULT_THEME_COLORS.secondary,
    ...(menuHover ? { menuHover } : {}),
  };
}

function applyThemeColors(colors: ResolvedThemeColors) {
  if (!import.meta.client) {
    return;
  }

  const key = colorsKey(colors);
  if (key === appliedKey) {
    return;
  }

  // PrimeVue generates every --p-primary-* / --p-secondary-* variable, in light and dark.
  // Nothing is written inline on <html>: inline values would override `.dark`.
  const secondary = buildSecondaryScale(colors.secondary);
  updatePreset({
    semantic: {
      primary: buildPrimaryScale(colors.primary),
      secondary: {
        ...secondary,
        soft: colors.menuHover ?? DEFAULT_MENU_HOVER,
        contrastColor: getContrastColor(secondary[500]),
      },
    },
  });
  appliedKey = key;
}

export function useUserThemeColors() {
  const userPreferenceStore = useUserPreferenceStore();
  const preferenceApi = usePreferenceApiService();

  function getThemeColors(): ResolvedThemeColors {
    return resolveThemeColors(userPreferenceStore.preference.theme?.colors);
  }

  function previewThemeColors(colors: UserThemeColors) {
    applyThemeColors(resolveThemeColors(colors));
  }

  async function saveThemeColors(colors: UserThemeColors) {
    const previousColors = getThemeColors();
    const nextColors = resolveThemeColors(colors);

    userPreferenceStore.updateThemePreference({
      colors: nextColors,
    });
    applyThemeColors(nextColors);

    try {
      await preferenceApi.update(userPreferenceStore.preference);
    }
    catch (error) {
      userPreferenceStore.updateThemePreference({
        colors: previousColors,
      });
      applyThemeColors(previousColors);
      throw error;
    }
  }

  function initializeThemeColors() {
    if (!import.meta.client) {
      return;
    }

    // During setup PrimeVue has not rendered its theme styles yet: an updatePreset
    // made then is only painted on the next style load, seconds later.
    onMounted(() => applyThemeColors(getThemeColors()));

    if (!isThemeWatcherRegistered) {
      isThemeWatcherRegistered = true;
      userPreferenceStore.$subscribe(() => {
        applyThemeColors(getThemeColors());
      });
    }
  }

  return {
    defaultThemeColors: DEFAULT_THEME_COLORS,
    getThemeColors,
    initializeThemeColors,
    previewThemeColors,
    saveThemeColors,
  };
}
